/**
 * DuplicateContactModal — Shown when transferring a contact to an org
 * where similar contacts already exist.
 *
 * Options:
 * - Merge with an existing contact (combines data)
 * - Keep both (proceed with transfer anyway)
 * - Cancel (don't transfer)
 */

import React, { useState } from 'react';
import useModalBehavior from '../../hooks/useModalBehavior';
import { X, AlertTriangle, Users, ArrowRight, Check, Loader2 } from 'lucide-react';
import './DuplicateContactModal.css';
import OverlayPortal from '../shared/OverlayPortal';

function DuplicateContactModal({
  contact,
  duplicates,
  targetDeptName,
  onMerge,
  onProceed,
  onCancel,
  isMerging,
}) {
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);

  const fullName = `${contact.first_name} ${contact.last_name || ''}`.trim();

  const handleMerge = () => {
    if (selectedDuplicate) {
      onMerge(selectedDuplicate.id);
    }
  };

  const modal = useModalBehavior(onCancel);
  return (
    <OverlayPortal>
    <div className="dcm-overlay" {...modal.overlayProps}>
      <div className="dcm-modal" {...modal.dialogProps} aria-label="Possible duplicate contact" onClick={(e) => e.stopPropagation()}>
        <div className="dcm-header">
          <div className="dcm-header-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="dcm-header-text">
            <h2>Similar Contacts Found</h2>
            <p>
              <strong>{fullName}</strong> may already exist in {targetDeptName}
            </p>
          </div>
          <button className="dcm-close" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>

        <div className="dcm-body">
          <p className="dcm-instruction">
            Select a contact to merge with, or choose to keep both:
          </p>

          <div className="dcm-duplicates">
            {duplicates.map((dup) => {
              const dupName = `${dup.first_name} ${dup.last_name || ''}`.trim();
              const isSelected = selectedDuplicate?.id === dup.id;

              return (
                <button
                  key={dup.id}
                  className={`dcm-duplicate ${isSelected ? 'dcm-duplicate-selected' : ''}`}
                  onClick={() => setSelectedDuplicate(isSelected ? null : dup)}
                >
                  <div className="dcm-dup-check">
                    {isSelected && <Check size={14} />}
                  </div>
                  <div className="dcm-dup-info">
                    <span className="dcm-dup-name">{dupName}</span>
                    {dup.title && <span className="dcm-dup-title">{dup.title}</span>}
                    {dup.email && <span className="dcm-dup-email">{dup.email}</span>}
                  </div>
                  <div className="dcm-dup-meta">
                    {dup.relationship_status && (
                      <span className="dcm-dup-status">{dup.relationship_status}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {selectedDuplicate && (
            <div className="dcm-merge-preview">
              <Users size={14} />
              <span>
                {/* CRM-12: the SELECTED contact is the survivor; the one you're
                    viewing ({fullName}) is absorbed and removed — the copy used
                    to say the opposite. */}
                Merging keeps the selected contact and moves <strong>{fullName}</strong>'s
                notes and coverage into it. <strong>{fullName}</strong> is then removed
                (recoverable from trash).
              </span>
            </div>
          )}
        </div>

        <div className="dcm-footer">
          <button className="dcm-btn dcm-btn-cancel" onClick={onCancel} disabled={isMerging}>
            Cancel
          </button>
          <button
            className="dcm-btn dcm-btn-secondary"
            onClick={onProceed}
            disabled={isMerging}
          >
            <ArrowRight size={14} />
            Keep Both
          </button>
          <button
            className="dcm-btn dcm-btn-primary"
            onClick={handleMerge}
            disabled={!selectedDuplicate || isMerging}
          >
            {isMerging ? (
              <Loader2 size={14} className="dcm-spinner" />
            ) : (
              <Users size={14} />
            )}
            Merge Selected
          </button>
        </div>
      </div>
    </div>
    </OverlayPortal>
  );
}

export default DuplicateContactModal;
